// /src/components/Header.jsx

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStatus } from "../StatusContext";
import { useAuth } from "../authContext";
import { useSecurity } from "../SecurityContext";
import UserTooltip from "./UserTooltip";
import logo from "../medias/logo.png";
import SecondaryNavBar from "./private/SecondaryNavBar";
import user_default from "../medias/loggedinicon.svg";
import reload from "../medias/reload.png";
import HeaderRoutes from "./HeaderRoutes";
import LoadingSpinner from "./LoadingSpinner";
import { useVersion } from "../VersionContext";
import { forceHardReload } from "../../utils/forceHardReload";
import { setPendingVersionAck } from "../../utils/ensureFreshFrontendBundle";
import { isLocalDevEnvironment } from "../../utils/isLocalDevEnvironment";
import { bootstrapAuth } from "../bootstrapAuth";
import { clearBlockLoginAutoRedirect } from "../authSessionStorage";
import UserDiscordRankDisplay from "./tools/UserDiscordRankDisplay";
import AltAccountWarning from "./AltAccountWarning";
import { useActiveUserIsAltAccount } from "../hooks/useActiveUserIsAltAccount";
import { fetchMemberProfileCard } from "./tools/memberProfileCardApi";
import { buildRankChipDataAttrs } from "./tools/chipColorDataAttr";
import useRankChipCspStyles from "../hooks/useRankChipCspStyles";
import { useWebsiteAccessGate } from "../hooks/useWebsiteAccessGate";

export default function Header() {
  const {
    isLoggedIn,
    setIsLoggedIn,
    setDiscordName,
    discordName,
    handles,
    rankImageUrl,
    rankColors,
    userId,
    email,
    setEmail,
    setLanguage,
    language,
    injectAuth,
  } = useAuth();

  const {
    userHasPermission,
    realUserHasPermission,
    isViewAsActive,
    viewAsUserId,
    viewAsLoading,
    isSuperAdmin,
  } = useSecurity();
  const status = useStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { identityComplete, navUnlocked, orgMembershipEligible } = useWebsiteAccessGate();
  const { showAltAccountWarning } = useActiveUserIsAltAccount();

  const isMaintenance =
    status?.networkStatus === "offline" ||
    status?.serverStatus === "unreachable" ||
    (status?.maintenance === true && !isSuperAdmin);

  const maintenanceLock = isMaintenance ? { lockStatus: true } : null;
  const loading = false;

  const [showNavBar, setShowNavBar] = useState(true);
  const [lastScroll, setLastScroll] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [sessionRestorePending, setSessionRestorePending] = useState(false);

  const emailRef = useRef(null);

  const needsIdentityCheck = !isViewAsActive && !identityComplete;
  const inWebsiteAccessGate = !isViewAsActive && !navUnlocked;
  // Identity complete but not org-eligible must resume on identity check (membership notice), not onboarding.
  const gateResumeLink = inWebsiteAccessGate
    ? (identityComplete && orgMembershipEligible ? "onboarding" : "identity")
    : null;
  const forceTooltipLogout = inWebsiteAccessGate;

  const [viewAsProfile, setViewAsProfile] = useState(null);
  const [viewAsProfileLoading, setViewAsProfileLoading] = useState(false);
  const [selfProfileCard, setSelfProfileCard] = useState(null);

  useEffect(() => {
    if (!isViewAsActive || viewAsUserId == null) {
      setViewAsProfile(null);
      setViewAsProfileLoading(false);
      return undefined;
    }

    let cancelled = false;
    setViewAsProfileLoading(true);

    (async () => {
      try {
        const res = await fetchMemberProfileCard(viewAsUserId);
        if (cancelled) return;
        setViewAsProfile(res?.ok ? res.card : null);
      } catch {
        if (!cancelled) setViewAsProfile(null);
      } finally {
        if (!cancelled) setViewAsProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isViewAsActive, viewAsUserId]);

  useEffect(() => {
    if (!isLoggedIn || !userId || isViewAsActive) {
      setSelfProfileCard(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetchMemberProfileCard(userId);
        if (cancelled) return;
        setSelfProfileCard(res?.ok ? res.card : null);
      } catch {
        if (!cancelled) setSelfProfileCard(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId, isViewAsActive]);

  const activeRankColors = useMemo(() => {
    if (isViewAsActive) return viewAsProfile?.rankColors ?? null;
    return rankColors ?? selfProfileCard?.rankColors ?? null;
  }, [isViewAsActive, viewAsProfile, selfProfileCard, rankColors]);

  const activeRankImageUrl = useMemo(() => {
    if (isViewAsActive) return viewAsProfile?.rankImageUrl ?? null;
    return rankImageUrl ?? selfProfileCard?.rankImageUrl ?? null;
  }, [isViewAsActive, viewAsProfile, selfProfileCard, rankImageUrl]);

  const userChipRankAttrs = useMemo(
    () => buildRankChipDataAttrs(activeRankColors),
    [activeRankColors],
  );

  const hasRankChipTint = Boolean(userChipRankAttrs["data-chip-bg"]);

  useRankChipCspStyles(userChipRankAttrs["data-chip-bg"]);

  const viewAsPrimaryHandle = useMemo(() => {
    if (!viewAsProfile) return "";
    const fromCard = viewAsProfile.primaryHandle != null
      ? String(viewAsProfile.primaryHandle).trim()
      : "";
    if (fromCard) return fromCard;
    const handlesList = viewAsProfile.handles;
    if (!Array.isArray(handlesList) || handlesList.length === 0) return "";
    const master = handlesList.find((h) => h.isMaster === true);
    return String(master?.handle || handlesList[0]?.handle || "").trim();
  }, [viewAsProfile]);

  const viewAsDisplayName = useMemo(() => {
    if (!viewAsProfile) return "";
    const moniker = viewAsProfile.moniker != null ? String(viewAsProfile.moniker).trim() : "";
    if (moniker && !viewAsProfile.monikerRedacted) return moniker;
    const discord = viewAsProfile.discordName != null
      ? String(viewAsProfile.discordName).trim()
      : "";
    if (discord) return discord;
    return viewAsPrimaryHandle;
  }, [viewAsProfile, viewAsPrimaryHandle]);

  const masterHandle = useMemo(() => {
    if (!Array.isArray(handles) || handles.length === 0) return "";
    const master = handles.find((h) => h.isMaster === true);
    return String(master?.handle || handles[0]?.handle || "").trim();
  }, [handles]);

  const { hasUpdate, pendingVersion } = useVersion();
  const showVersionReloadButton = hasUpdate && !isLocalDevEnvironment();


  async function handleUpdateClick() {
    if (pendingVersion) {
      setPendingVersionAck(pendingVersion);
    }
    try {
      await forceHardReload();
    } catch (err) {
      console.error("Hard reload failed:", err);
      const url = new URL(window.location.href);
      url.searchParams.set("_hr", String(Date.now()));
      window.location.replace(url.toString());
    }
  }

  useEffect(() => {
    if (isLoggedIn && language) {
      i18n.changeLanguage(language);
    }
  }, [isLoggedIn, language, i18n]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      setShowNavBar(currentScroll <= 0 || currentScroll < lastScroll);
      setLastScroll(currentScroll);
    };

    const closeTooltip = (e) => {
      if (emailRef.current && !emailRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("click", closeTooltip);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", closeTooltip);
    };
  }, [lastScroll]);

  useEffect(() => {
    document.body.classList.toggle("isLoggedIn", isLoggedIn);
  }, [isLoggedIn]);

  const logout = async () => {
    try {
      await axios.post("/api/logout", { withCredentials: true });
    } catch (error) {
      console.error("Failed to log out on the server side", error);
    }

    setIsLoggedIn(false);
    setEmail(null);
    setLanguage("en");
    setDiscordName(null);
    i18n.changeLanguage("en");
    sessionStorage.removeItem("initialPath");
    navigate("/");
  };

  const handleLoginClick = async (event) => {
    event.preventDefault();
    if (sessionRestorePending) return;

    setSessionRestorePending(true);
    try {
      const { user } = await bootstrapAuth();
      if (user?.isLoggedIn) {
        clearBlockLoginAutoRedirect();
        injectAuth(user);
        return;
      }
      navigate("/login");
    } catch (err) {
      console.warn("[Header] Session restore failed:", err?.message || err);
      navigate("/login");
    } finally {
      setSessionRestorePending(false);
    }
  };

  return (
    <div
      className={[
        "header-root",
        showTooltip ? "header-root--user-tooltip-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      key={loading ? "header-spinner" : "header"}
    >
      {loading ? (
        <LoadingSpinner />
      ) : maintenanceLock?.lockStatus && !isLoggedIn ? (
        null
      ) : (
        <div className="header-bar">
          <div className="header-container">
            <header className="header">
              <div className="logo">
                <img src={logo} alt="Logo" />
              </div>
              <div className="brand">
                <span>PALLAS </span>
                <span> INVICTUS</span>
              </div>
              <div className="underheader-container">
                <div className="underheader-polygon"></div>
              </div>
            </header>

            <nav className={`menu ${!showNavBar ? "hide-menu" : ""}`}>
              <HeaderRoutes />

              <div className={`login-btn ${isLoggedIn ? "logged-in" : "logged-out"}`}>
                {isLoggedIn ? (
                  <>
                    <span
                      className={`login-btn__user-chip-wrap${isViewAsActive ? " login-btn__user-chip-wrap--view-as" : ""}`}
                    >
                      {isViewAsActive ? (
                        <span className="login-btn__view-as-label">view as</span>
                      ) : null}
                      {showAltAccountWarning ? (
                        <AltAccountWarning />
                      ) : null}
                      <span
                        className={[
                          "email",
                          "user-chip",
                          hasRankChipTint ? "user-chip--rank-tinted" : "",
                          showTooltip ? "is-open" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        ref={emailRef}
                        onClick={() => setShowTooltip(!showTooltip)}
                        {...userChipRankAttrs}
                      >
                        <img
                          src={user_default}
                          className="user-avatar-mobile"
                          alt=""
                        />

                        {isViewAsActive ? (
                          viewAsProfileLoading || viewAsLoading ? (
                            <span className="email-text">loading…</span>
                          ) : (
                            <UserDiscordRankDisplay
                              discordName={viewAsDisplayName}
                              handle={viewAsPrimaryHandle || undefined}
                              rankImageUrl={viewAsProfile?.rankImageUrl}
                              nameFallback={viewAsPrimaryHandle || viewAsDisplayName}
                              hideAvatar
                              rankBeforeName
                              compact
                              className="header-user-identity"
                            />
                          )
                        ) : needsIdentityCheck ? (
                          <span className="email-text">onboarding...</span>
                        ) : (
                          <UserDiscordRankDisplay
                            discordName={discordName}
                            handle={masterHandle || undefined}
                            rankImageUrl={activeRankImageUrl}
                            rankColors={activeRankColors}
                            nameFallback={masterHandle}
                            hideAvatar
                            rankBeforeName
                            compact
                            className="header-user-identity"
                          />
                        )}

                        <span
                          className={`user-chip__chevron ${showTooltip ? "is-open" : ""}`}
                          aria-hidden="true"
                        >
                          ⌄
                        </span>
                      </span>
                    </span>

                    {showTooltip && (
                      <UserTooltip
                        userHasPermission={userHasPermission}
                        realUserHasPermission={realUserHasPermission}
                        onLogout={logout}
                        t={t}
                        logoutOnly={inWebsiteAccessGate}
                        gateResumeLink={gateResumeLink}
                        forceLogout={forceTooltipLogout}
                        chipBorderAttrs={userChipRankAttrs}
                      />
                    )}
                  </>
                ) : (
                  <Link
                    className="link-login"
                    to="/login"
                    onClick={handleLoginClick}
                    aria-busy={sessionRestorePending}
                  >
                    {sessionRestorePending ? "…" : t("Login")}
                  </Link>
                )}

                {showVersionReloadButton && (
                  <button
                    onClick={handleUpdateClick}
                    className="new-version-update"
                    data-tooltip="New version is available!"
                  >
                    <img
                      src={reload}
                      className="reload-icon"
                      alt="Reload"
                    />
                  </button>
                )}
              </div>
            </nav>
          </div>

          {isLoggedIn && <SecondaryNavBar />}
        </div>
      )}
    </div>
  );
}
